import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import { useDataProvider, useNotify, useRefresh, useRedirect } from 'react-admin';

const SectionCardList = ({ sectionId }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadItems = useCallback(() => {
    if (!sectionId) return;
    setLoading(true);
    // Envia apenas o filtro necessário
    const filter = {
      company: localStorage.getItem('company'),
      section_id: String(sectionId),
    };
    dataProvider
      .getList('section_informations', {
        filter,
        sort: { field: 'order', order: 'ASC' },
        pagination: { page: 1, perPage: 100 },
      })
      .then(({ data }) => setItems(data))
      .catch((error) => {
        setItems([]);
        notify(
          error?.body?.message || 'Erro ao carregar itens da seção',
          { type: 'error' },
        );
      })
      .finally(() => setLoading(false));
  }, [sectionId, dataProvider, notify]);

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId]);

  const handleToggle = (item) => {
    setLoading(true);
    dataProvider
      .update('section_informations', {
        id: item.id,
        data: { ...item, active: !item.active },
      })
      .then(() => {
        notify('Status alterado com sucesso', { type: 'success' });
        refresh();
      })
      .catch((error) => {
        notify(
          error?.body?.message || 'Erro ao alterar status',
          { type: 'error' },
        );
      })
      .finally(() => setLoading(false));
  };

  const handleDelete = (item) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Tem certeza que deseja excluir este item?')) {
      dataProvider
        .delete('section_informations', { id: item.id })
        .then(() => {
          notify('Item excluído com sucesso', { type: 'success' });
          loadItems();
        })
        .catch(() => notify('Erro ao excluir item', { type: 'error' }));
    }
  };

  if (!sectionId) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6" color="textSecondary">
          Selecione uma seção na árvore para visualizar os itens
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6" color="textSecondary">
          Nenhum item encontrado nesta seção
        </Typography>
        <Button 
          variant="contained" 
          sx={{ mt: 2 }}
          onClick={() => redirect('/section_informations/create')}
        >
          Criar Primeiro Item
        </Button>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {items.map(item => (
        <Grid item xs={12} sm={6} md={4} key={item.id}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Typography variant="h6" component="h3" noWrap>
                  {item.section_data?.title || item.title || `Item ${item.id}`}
                </Typography>
                <Chip 
                  label={item.disabled ? 'Inativo' : 'Ativo'} 
                  color={item.disabled ? 'error' : 'success'}
                  size="small"
                />
              </Box>
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Ordem: {item.order}
              </Typography>
              
              {item.section_data?.titulo && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {item.section_data.titulo.length > 100
                    ? `${item.section_data.titulo.substring(0, 100)}...`
                    : item.section_data.titulo
                  }
                </Typography>
              )}
              
              {item.section_data?.description && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {item.section_data.description.length > 100 
                    ? `${item.section_data.description.substring(0, 100)}...`
                    : item.section_data.description
                  }
                </Typography>
              )}
              
              <Typography variant="caption" color="textSecondary">
                Criado: {new Date(item.createdAt).toLocaleDateString('pt-BR')}
              </Typography>
            </CardContent>
            
            <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
              <Box>
                <Tooltip title="Editar">
                  <IconButton 
                    color="primary"
                    onClick={() => redirect(`/section_informations/${item.id}`)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Box>
                <Tooltip title={item.disabled ? 'Ativar' : 'Desativar'}>
                  <IconButton 
                    color={item.disabled ? 'success' : 'warning'}
                    onClick={() => handleToggle(item)}
                  >
                    {item.disabled ? <ToggleOffIcon /> : <ToggleOnIcon />}
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Excluir">
                  <IconButton 
                    color="error"
                    onClick={() => handleDelete(item)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default SectionCardList;
